"use client";

import type { FC } from "react";
import type { Segment, Word } from "@ui/components/demos/STT/types";

import { clsx } from "clsx";
import { type MotionValue, useMotionValue, useTransform } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useAutoScrollToElement } from "@/app/_ui/hooks/useAutoScrollToElement";

export interface STTHeroTranscriptProps {
  currentTime: MotionValue<number>;
  segments: Segment[];
  onWordClick?: (time: number) => void;
  isPlaying?: boolean;
  /** Pixels reserved at the bottom of the container (e.g. a gradient overlay) */
  bottomOffset?: number;
}

export const STTHeroTranscript: FC<STTHeroTranscriptProps> = ({
  currentTime,
  segments,
  onWordClick,
  isPlaying = false,
  bottomOffset = 0,
}) => {
  const t = useTranslations("ClientKeys.SpeechToText");
  const wordRefs = useRef<Map<string, HTMLElement>>(new Map());
  const lastScrolledWordRef = useRef<string | null>(null);
  const [fadeTop, setFadeTop] = useState(false);

  const { scrollContainerRef, scrollToElement } = useAutoScrollToElement<HTMLDivElement>({
    enabled: isPlaying,
    bottomOffset,
  });

  const updateTopFade = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setFadeTop(el.scrollTop > 2);
  }, [scrollContainerRef]);

  useLayoutEffect(() => {
    updateTopFade();
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateTopFade, { passive: true });
    const ro = new ResizeObserver(updateTopFade);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateTopFade);
      ro.disconnect();
    };
  }, [segments.length, updateTopFade]);

  useEffect(() => {
    if (!isPlaying || segments.length === 0) return;

    const unsubscribe = currentTime.on("change", time => {
      let activeWordId: string | null = null;
      outer: for (const segment of segments) {
        for (const word of segment.words) {
          if (word.type !== "word") continue;
          if (time >= word.start && time <= word.end) {
            activeWordId = word.id ?? null;
            break outer;
          }
        }
      }

      if (activeWordId && activeWordId !== lastScrolledWordRef.current) {
        lastScrolledWordRef.current = activeWordId;
        const element = wordRefs.current.get(activeWordId);
        if (element) scrollToElement(element);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isPlaying, segments, currentTime, scrollToElement]);

  if (!segments.length) {
    return (
      <div className='tw-flex-1 tw-min-h-0 tw-flex tw-items-center tw-justify-center tw-text-black/40'>
        {t("widget.no_transcript.title")}
      </div>
    );
  }

  return (
    <div className='tw-relative tw-flex-1 tw-min-h-0 tw-min-w-0 tw-flex tw-flex-col tw-overflow-hidden'>
      <div
        className={clsx(
          "tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-z-10 tw-h-12 tw-transition-opacity tw-duration-300",
          !fadeTop && "tw-opacity-0",
        )}
        style={{ background: "linear-gradient(to bottom, rgb(255 255 255), transparent)" }}
        aria-hidden
      />
      <div
        ref={scrollContainerRef}
        className='tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-flex tw-flex-col tw-gap-6 tw-px-2 tw-pt-3 tw-pb-16 tw-scrollbar-none'
      >
        {segments.map(segment => {
          const speakerNum = segment.speaker_id ? parseInt(segment.speaker_id.replace("speaker_", ""), 10) + 1 : 1;
          const totalSecs = Math.floor(segment.start);
          const mins = Math.floor(totalSecs / 60);
          const secs = totalSecs % 60;
          const timestamp = `${mins}:${secs.toString().padStart(2, "0")}`;

          return (
            <div key={segment.id} className='tw-flex tw-flex-col tw-gap-[13px]'>
              <p className='tw-type-xs tw-font-medium tw-text-black/60'>
                {t("widget.speaker")} {speakerNum}
                <span aria-hidden='true'> • </span>
                {timestamp}
              </p>
              <p className='tw-type-sm'>
                {segment.words.map((word: Word) => (
                  <HeroHighlightedWord
                    key={word.id}
                    word={word}
                    currentTime={currentTime}
                    onWordClick={onWordClick}
                    wordRefs={wordRefs.current}
                  />
                ))}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface HeroHighlightedWordProps {
  word: Word;
  currentTime: MotionValue<number>;
  onWordClick?: (time: number) => void;
  wordRefs: Map<string, HTMLElement>;
}

const HeroHighlightedWord: FC<HeroHighlightedWordProps> = ({ word, currentTime, onWordClick, wordRefs }) => {
  const isHovered = useMotionValue(0);
  const isActive = word.type === "word";

  const opacity = useTransform([currentTime, isHovered] as MotionValue<number>[], ([time, hovered]: number[]) =>
    hovered === 1 || time >= word.start ? 1 : 0.4,
  );

  const backgroundColor = useTransform(currentTime, (time: number) =>
    isActive && time >= word.start && time <= word.end ? "rgba(4, 71, 255, 0.1)" : "rgba(4, 71, 255, 0)",
  );

  const color = useTransform(currentTime, (time: number) =>
    isActive && time >= word.start && time <= word.end ? "#0447ff" : "rgb(0, 0, 0)",
  );

  return (
    <m.span
      ref={el => {
        if (word.id && word.type === "word") {
          if (el) wordRefs.set(word.id, el);
          else wordRefs.delete(word.id);
        }
      }}
      onClick={() => onWordClick?.(word.start)}
      onPointerEnter={() => isHovered.set(1)}
      onPointerLeave={() => isHovered.set(0)}
      className='tw-cursor-pointer tw-rounded-[4px]'
      style={{ opacity, backgroundColor, color }}
    >
      {word.text}
    </m.span>
  );
};
